import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Loader2, Users, GraduationCap, Layers, Award, TrendingUp, TrendingDown, Target, Plus, Trash2,
  CalendarClock, CheckSquare, Square, Megaphone, Sparkles, Rocket, DollarSign, Filter, Download, Repeat, Copy, Link2,
  MessageSquare, ExternalLink, Check, X, Lightbulb, BookOpen, PlayCircle, Printer, Search, ArrowRight, RefreshCw, History, Library, Folder, Film, Route, ListOrdered, GripVertical, ThumbsUp, HelpCircle, KeyRound, Send, Snowflake,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import VRViewer from "@/components/VRViewer";
import { printLesson } from "@/lib/printLesson";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import api, { imgUrl } from "@/lib/api";

const PLATFORMS = ["Instagram", "Facebook", "X (Twitter)", "LinkedIn", "TikTok", "YouTube"];
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Delta({ v }) {
  if (v == null) return null;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${up ? "text-emerald-600" : "text-red-600"}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{Math.abs(v)}%
    </span>
  );
}

function BeatRow({ beat, onChange, onRemove }) {
  const controls = useDragControls();
  const label =
    beat.type === "hotspot" ? "Hotspot reveal"
      : beat.type === "checkin" ? (beat.kind === "mcq" ? "Check-in · quiz" : "Check-in · pulse")
        : "Narration";
  const color =
    beat.type === "hotspot" ? "bg-cyan-500/15 text-cyan-700"
      : beat.type === "checkin" ? "bg-amber-500/15 text-amber-700"
        : "bg-primary/15 text-primary";
  const val = beat.type === "checkin" ? beat.question || beat.prompt || "" : beat.text || "";
  return (
    <Reorder.Item
      value={beat}
      dragListener={false}
      dragControls={controls}
      className="rounded-xl border border-border bg-card p-2.5 flex items-start gap-2"
      data-testid="beat-item"
    >
      <span
        onPointerDown={(e) => controls.start(e)}
        className="mt-2 cursor-grab shrink-0 touch-none text-muted-foreground"
        data-testid="beat-drag-handle"
      >
        <GripVertical className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <span className={`inline-block text-[9px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 mb-1 ${color}`}>{label}</span>
        <textarea
          value={val}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-h-[52px] rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          data-testid="beat-text-input"
        />
      </div>
      <button onClick={onRemove} className="mt-1 h-7 w-7 flex items-center justify-center rounded-md text-destructive hover:bg-destructive/10 shrink-0" data-testid="beat-remove-btn">
        <Trash2 className="w-4 h-4" />
      </button>
    </Reorder.Item>
  );
}


export default function CompanyDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [ov, setOv] = useState(null);
  const [goals, setGoals] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", due: "" });
  const [newMeeting, setNewMeeting] = useState({ title: "", when: "", attendees: "" });
  const [camp, setCamp] = useState({ name: "", platform: "Instagram", objective: "", budget: "", audience: "" });
  const [genLoading, setGenLoading] = useState(false);
  const [variants, setVariants] = useState(null);
  const [image, setImage] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [contribs, setContribs] = useState([]);
  const [contribFilter, setContribFilter] = useState("pending_review");
  const [settings, setSettings] = useState({ skool_url: "" });
  const [allLessons, setAllLessons] = useState([]);
  const [launchLesson, setLaunchLesson] = useState(null);
  const [lessonSearch, setLessonSearch] = useState("");
  const [lessonSubject, setLessonSubject] = useState("all");
  const [lessonGrade, setLessonGrade] = useState("all");
  const [libGroupBy, setLibGroupBy] = useState("status");
  const [reviewLesson, setReviewLesson] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [checkinInsights, setCheckinInsights] = useState([]);
  const [adminSug, setAdminSug] = useState("");
  const [regenRunning, setRegenRunning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [spatializing, setSpatializing] = useState(null);
  const [sequencing, setSequencing] = useState(null);
  const [guiding, setGuiding] = useState(null);
  const [includeCheckins, setIncludeCheckins] = useState(false);
  const [beatEditorOpen, setBeatEditorOpen] = useState(false);
  const [editBeats, setEditBeats] = useState([]);
  const [editSceneIdx, setEditSceneIdx] = useState(0);
  const [savingBeats, setSavingBeats] = useState(false);
  const [users, setUsers] = useState([]);
  const [trash, setTrash] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deleteUser, setDeleteUser] = useState(null);
  const [eraseData, setEraseData] = useState(false);
  const [tempPwResult, setTempPwResult] = useState(null);

  const load = async (d = days) => {
    try {
      const [o, g, t, m, c, s, al] = await Promise.all([
        api.get(`/company/overview?days=${d}`), api.get("/company/goals"),
        api.get("/company/tasks"), api.get("/company/meetings"), api.get("/company/campaigns"),
        api.get("/company/settings"), api.get("/all-lessons"),
      ]);
      setOv(o.data); setGoals(g.data); setTasks(t.data); setMeetings(m.data); setCampaigns(c.data);
      setSettings({ skool_url: s.data?.skool_url || "" });
      setAllLessons(al.data || []);
      loadContribs(contribFilter);
      loadAccounts();
    } catch (e) { toast.error("Failed to load company data"); }
    finally { setLoading(false); }
  };
  const loadAccounts = async () => {
    try {
      const [u, tr] = await Promise.all([api.get("/company/users"), api.get("/company/trash")]);
      setUsers(u.data || []);
      setTrash(tr.data || []);
    } catch (e) { /* non-blocking */ }
  };
  const loadContribs = async (status = contribFilter) => {
    try {
      const { data } = await api.get(`/company/cohort-contributions?status=${status}`);
      setContribs(data || []);
    } catch (e) { /* non-blocking */ }
  };
  useEffect(() => { load(); }, []);
  const changeRange = (d) => { setDays(+d); load(+d); };
  const changeContribFilter = (s) => { setContribFilter(s); loadContribs(s); };
  const approveContrib = async (id) => {
    await api.post(`/company/cohort-contributions/${id}/approve`, {});
    toast.success("Approved — feeding generator + published to library");
    loadContribs(); load();
  };
  const rejectContrib = async (id) => {
    await api.post(`/company/cohort-contributions/${id}/reject`, {});
    toast.success("Contribution rejected");
    loadContribs(); load();
  };
  const saveSkool = async () => {
    await api.put("/company/settings", { skool_url: settings.skool_url });
    toast.success("Skool community link saved");
  };
  const openSkool = () => {
    const u = (settings.skool_url || "").trim();
    if (!u) { toast.error("Add your Skool community URL first"); return; }
    window.open(u.startsWith("http") ? u : `https://${u}`, "_blank", "noopener");
  };

  const doResetPw = async (u, method) => {
    try {
      const { data } = await api.post(`/company/users/${u.id}/reset-password`, { method, app_base_url: window.location.origin });
      if (method === "temp") setTempPwResult({ email: u.email, password: data.temp_password, emailed: data.emailed });
      else toast.success(data.emailed ? `Reset link emailed to ${u.email}` : "Reset link created (email not configured)");
    } catch (e) { toast.error("Couldn't reset password"); }
  };
  const doFreeze = async (u, frozen) => {
    try { await api.post(`/company/users/${u.id}/freeze`, { frozen }); toast.success(frozen ? "Account frozen" : "Account unfrozen"); loadAccounts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update account"); }
  };
  const doChangeRole = async (u, role) => {
    try { await api.put(`/company/users/${u.id}`, { role }); toast.success("Role updated"); loadAccounts(); }
    catch (e) { toast.error("Couldn't update role"); }
  };
  const doChangePlan = async (u, plan) => {
    try { await api.put(`/company/users/${u.id}`, { plan }); toast.success("Plan updated"); loadAccounts(); }
    catch (e) { toast.error("Couldn't update plan"); }
  };
  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/company/users/${deleteUser.id}?erase_data=${eraseData}`);
      toast.success("Account deleted");
      setDeleteUser(null); setEraseData(false); loadAccounts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete account"); }
  };
  const restoreLesson = async (id) => {
    try { await api.post(`/company/lessons/${id}/restore`); toast.success("Lesson restored"); loadAccounts(); load(); }
    catch (e) { toast.error("Couldn't restore lesson"); }
  };
  const purgeLesson = async (id) => {
    try { await api.delete(`/company/lessons/${id}/purge`); toast.success("Permanently deleted"); loadAccounts(); }
    catch (e) { toast.error("Couldn't delete permanently"); }
  };

  const openReview = async (l) => {
    setReviewLesson(l);
    setReviewData(null);
    setAdminSug("");
    setCheckinInsights([]);
    setReviewLoading(true);
    try {
      const { data } = await api.get(`/company/lessons/${l.id}/review`);
      setReviewData(data);
      api.get(`/lessons/${l.id}/checkin-insights`).then(({ data: ci }) => setCheckinInsights(ci || [])).catch(() => {});
    } catch (e) { toast.error("Couldn't load lesson review"); }
    finally { setReviewLoading(false); }
  };
  const _adminLines = () => adminSug.split("\n").map((s) => s.trim()).filter(Boolean);
  const doRegenerate = async () => {
    if (!reviewLesson) return;
    const suggestions = [...(reviewData?.cohort_suggestions || []), ..._adminLines()];
    setRegenRunning(true);
    try {
      await api.post(`/company/lessons/${reviewLesson.id}/regenerate`, { suggestions });
      toast.info("Regenerating with the suggested edits — this can take a minute…");
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/company/lessons/${reviewLesson.id}/regen-status`);
          if (data.regen_status === "done") {
            clearInterval(poll); setRegenRunning(false);
            toast.success("Lesson regenerated with the edits");
            openReview(reviewLesson); load();
          } else if (data.regen_status === "error") {
            clearInterval(poll); setRegenRunning(false);
            toast.error("Regeneration failed — check the LLM budget and try again");
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
    } catch (e) { setRegenRunning(false); toast.error("Couldn't start regeneration"); }
  };
  const doApprove = async () => {
    if (!reviewLesson) return;
    setApproving(true);
    try {
      await api.post(`/company/lessons/${reviewLesson.id}/approve-to-library`, { extra_suggestions: _adminLines() });
      toast.success("Approved and posted to the library");
      setReviewLesson(null); load();
    } catch (e) { toast.error("Approval failed"); }
    finally { setApproving(false); }
  };

  const addDepth = async (l) => {
    setSpatializing(l.id);
    toast.info("Adding spatial depth — generating layers & hotspot art (~1 min)…");
    try {
      await api.post(`/lessons/${l.id}/spatialize`);
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/lessons/${l.id}/spatial-status`);
          if (data.spatial_status === "done") {
            clearInterval(poll); setSpatializing(null);
            toast.success("Depth added — launch the lesson to see the layered scene");
            load();
            if (reviewLesson && reviewLesson.id === l.id) openReview(reviewLesson);
          } else if (data.spatial_status === "error") {
            clearInterval(poll); setSpatializing(null);
            toast.error("Couldn't add depth — check the LLM budget and try again");
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (e) { setSpatializing(null); toast.error("Couldn't start depth generation"); }
  };

  const sequenceBeats = async (l, includeCheckins = false) => {
    setSequencing(l.id);
    toast.info(includeCheckins
      ? "Sequencing a guided timeline with pause & ask check-ins (~1 min)…"
      : "Sequencing a guided timeline — ordering each scene into beats (~1 min)…");
    try {
      await api.post(`/lessons/${l.id}/sequence-beats`, { include_checkins: includeCheckins });
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/lessons/${l.id}/sequence-status`);
          if (data.sequence_status === "done") {
            clearInterval(poll); setSequencing(null);
            toast.success("Guided timeline ready — launch and tap “Guided lesson”");
            load();
            if (reviewLesson && reviewLesson.id === l.id) openReview(reviewLesson);
          } else if (data.sequence_status === "error") {
            clearInterval(poll); setSequencing(null);
            toast.error("Couldn't sequence beats — check the LLM budget and try again");
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (e) { setSequencing(null); toast.error("Couldn't start beat sequencing"); }
  };

  const addGuide = async (l) => {
    setGuiding(l.id);
    toast.info("Creating a talking guide that matches the lesson's art style (~1 min)…");
    try {
      await api.post(`/lessons/${l.id}/add-guide`);
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/lessons/${l.id}/guide-status`);
          if (data.guide_status === "done") {
            clearInterval(poll); setGuiding(null);
            toast.success("Talking guide added — launch the lesson to meet your guide");
            load();
            if (reviewLesson && reviewLesson.id === l.id) openReview(reviewLesson);
          } else if (data.guide_status === "error") {
            clearInterval(poll); setGuiding(null);
            toast.error("Couldn't create the guide — check the LLM budget and try again");
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setGuiding(null);
      if (e?.response?.status === 402) toast.error("The talking guide is a Founding-tier feature.");
      else toast.error("Couldn't start guide creation");
    }
  };

  const openBeatEditor = () => {
    const vr = reviewData?.lesson?.vr_scene || {};
    const scenes = vr.scenes && vr.scenes.length ? vr.scenes : [vr];
    let n = 0;
    const built = scenes.map((sc) => {
      const raw = (Array.isArray(sc.beats) && sc.beats.length)
        ? sc.beats.map((b) => ({ ...b }))
        : (() => {
          const b = [];
          if (sc.narration) b.push({ type: "narration", text: sc.narration });
          (sc.hotspots || []).forEach((h, i) => b.push({ type: "hotspot", hotspot_index: i, text: `${h.title || ""}. ${h.info || ""}`.trim() }));
          return b;
        })();
      return raw.map((b) => ({ ...b, _id: `b${n++}` }));
    });
    setEditBeats(built);
    setEditSceneIdx(0);
    setBeatEditorOpen(true);
  };

  const reorderScene = (newOrder) =>
    setEditBeats((prev) => prev.map((s, i) => (i === editSceneIdx ? newOrder : s)));

  const updateBeat = (bi, value) =>
    setEditBeats((prev) => prev.map((scene, si) => (si !== editSceneIdx ? scene : scene.map((b, i) => {
      if (i !== bi) return b;
      if (b.type === "checkin") return b.question ? { ...b, question: value } : { ...b, prompt: value };
      return { ...b, text: value };
    }))));

  const removeBeat = (bi) =>
    setEditBeats((prev) => prev.map((scene, si) => (si !== editSceneIdx ? scene : scene.filter((_, i) => i !== bi))));

  const saveBeats = async () => {
    if (!reviewLesson) return;
    setSavingBeats(true);
    try {
      const clean = editBeats.map((s) => s.map(({ _id, ...rest }) => rest));
      await api.post(`/company/lessons/${reviewLesson.id}/save-beats`, { beats_by_scene: clean });
      toast.success("Timeline saved");
      setBeatEditorOpen(false);
      openReview(reviewLesson);
    } catch (e) { toast.error("Couldn't save the timeline"); }
    finally { setSavingBeats(false); }
  };


  const saveGoals = async () => { await api.put("/company/goals", goals); toast.success("Goals saved"); };
  const addTask = async () => { if (!newTask.title.trim()) return; await api.post("/company/tasks", newTask); setNewTask({ title: "", due: "" }); load(); };
  const toggleTask = async (t) => { await api.put(`/company/tasks/${t.id}`, { done: !t.done }); load(); };
  const delTask = async (id) => { await api.delete(`/company/tasks/${id}`); load(); };
  const addMeeting = async () => { if (!newMeeting.title.trim() || !newMeeting.when) { toast.error("Add a title and time"); return; } await api.post("/company/meetings", newMeeting); setNewMeeting({ title: "", when: "", attendees: "" }); load(); };
  const delMeeting = async (id) => { await api.delete(`/company/meetings/${id}`); load(); };

  const generateCopy = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/company/ad-copy", { platform: camp.platform, objective: camp.objective, audience: camp.audience, generate_image: true });
      setVariants(data.variants || []); setImage(data.image_url || null); setChosen((data.variants || [])[0] || null);
      toast.success("3 AI variants ready — pick one, then save");
    } catch (e) { toast.error("Copy generation failed"); }
    finally { setGenLoading(false); }
  };
  const utmFor = (name, platform) => `https://www.vividvrlessons.com/?utm_source=${slug(platform)}&utm_medium=social&utm_campaign=${slug(name) || "campaign"}`;
  const saveCampaign = async () => {
    if (!camp.name.trim()) { toast.error("Name the campaign"); return; }
    await api.post("/company/campaigns", {
      ...camp, budget: parseFloat(camp.budget) || 0,
      headline: chosen?.headline || "", body: chosen?.body || "", cta: chosen?.cta || "",
      hashtags: chosen?.hashtags || "", image_url: image || null, utm: utmFor(camp.name, camp.platform),
    });
    setCamp({ name: "", platform: "Instagram", objective: "", budget: "", audience: "" }); setVariants(null); setImage(null); setChosen(null);
    toast.success("Campaign saved"); load();
  };
  const launch = async (id) => { await api.post(`/company/campaigns/${id}/launch`); toast.success("Campaign launched (demo)"); load(); };
  const delCampaign = async (id) => { await api.delete(`/company/campaigns/${id}`); load(); };
  const saveMetrics = async (c, m) => { await api.put(`/company/campaigns/${c.id}`, m); toast.success("Metrics updated"); load(); };
  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const exportCsv = () => {
    const rows = [["Metric", "Value"],
      ["Teachers", ov.teachers], ["Students", ov.students], ["Cohort", ov.cohort], ["Lessons", ov.lessons],
      ["Avg quiz score %", ov.avg_quiz_score], ["Attendance %", ov.attendance_rate],
      ["Signups (period)", ov.deltas.signups_cur], ["Library lessons", ov.library], ["Tips feeding generator", ov.approved_tips]];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "vividvr-board-report.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return (<div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>);

  const revPct = goals?.revenue_goal ? Math.min(100, Math.round((goals.revenue_actual / goals.revenue_goal) * 100)) : 0;
  const salesPct = goals?.sales_goal ? Math.min(100, Math.round((goals.sales_actual / goals.sales_goal) * 100)) : 0;
  const maxFunnel = Math.max(1, ...ov.funnel.map((f) => f.count));
  const KPIS = [
    { label: "Teachers", val: ov.teachers, icon: Users, c: "text-primary bg-primary/10", d: null },
    { label: "Students", val: ov.students, icon: GraduationCap, c: "text-cyan-600 bg-cyan-500/10", d: null },
    { label: `Signups (${days}d)`, val: ov.deltas.signups_cur, icon: TrendingUp, c: "text-emerald-600 bg-emerald-500/10", d: ov.deltas.signups },
    { label: `Lessons (${days}d)`, val: ov.deltas.lessons_cur, icon: Layers, c: "text-accent bg-accent/10", d: ov.deltas.lessons },
    { label: "Avg quiz score", val: `${ov.avg_quiz_score}%`, icon: Award, c: "text-amber-600 bg-amber-500/10", d: null },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Badge className="bg-slate-900 text-white border-0 mb-2">Company Ops</Badge>
            <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">VividVR command center</h1>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={changeRange}>
              <SelectTrigger data-testid="range-select" className="h-10 w-32"><Filter className="w-4 h-4 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>{[7, 14, 30, 90].map((d) => <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" data-testid="export-csv-btn" onClick={exportCsv} className="rounded-full h-10"><Download className="w-4 h-4 mr-1" /> Export</Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid grid-cols-6 w-full max-w-3xl">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-library">Library</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
            <TabsTrigger value="cohort" data-testid="tab-cohort">Cohort</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts</TabsTrigger>
            <TabsTrigger value="ops" data-testid="tab-ops">Ops</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {KPIS.map((k) => (
                <div key={k.label} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between"><div className={`w-11 h-11 rounded-xl flex items-center justify-center ${k.c}`}><k.icon className="w-5 h-5" /></div><Delta v={k.d} /></div>
                  <div className="text-3xl font-display font-extrabold mt-3">{k.val}</div>
                  <div className="text-sm text-muted-foreground">{k.label}</div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">New signups · last {days} days</h2></div>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={ov.signups.map((s) => ({ day: new Date(s.day).toLocaleDateString("en-US", { month: "short", day: "numeric" }), count: s.count }))} margin={{ left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(244 25% 90%)" vertical={false} />
                    <XAxis dataKey="day" fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip /><Bar dataKey="count" fill="hsl(244 68% 61%)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6" data-testid="company-goals">
                <div className="flex items-center gap-2 mb-4"><Target className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Goals · {goals.period}</h2></div>
                <div className="space-y-3">
                  <div><div className="flex justify-between text-sm mb-1"><span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Revenue</span><span className="font-semibold">${goals.revenue_actual}/${goals.revenue_goal}</span></div><Progress value={revPct} className="h-2" /></div>
                  <div><div className="flex justify-between text-sm mb-1"><span>Sales</span><span className="font-semibold">{goals.sales_actual}/{goals.sales_goal}</span></div><Progress value={salesPct} className="h-2" /></div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Input data-testid="goal-rev-goal" type="number" value={goals.revenue_goal} onChange={(e) => setGoals({ ...goals, revenue_goal: +e.target.value })} className="h-9" placeholder="Rev goal" />
                    <Input type="number" value={goals.sales_goal} onChange={(e) => setGoals({ ...goals, sales_goal: +e.target.value })} className="h-9" placeholder="Sales goal" />
                  </div>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3 text-primary" /> Revenue & sales sync automatically from paid Stripe checkouts.</p>
                  <Button data-testid="goal-save-btn" onClick={saveGoals} className="w-full rounded-full h-9">Save goals</Button>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Funnel */}
              <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6" data-testid="activation-funnel">
                <h2 className="font-display font-bold text-lg mb-4">Activation funnel</h2>
                <div className="space-y-3">
                  {ov.funnel.map((f, i) => {
                    const prev = i === 0 ? f.count : ov.funnel[i - 1].count;
                    const conv = prev ? Math.round((f.count / prev) * 100) : 0;
                    return (
                      <div key={f.step}>
                        <div className="flex justify-between text-sm mb-1"><span>{f.step}</span><span className="font-semibold">{f.count}{i > 0 && <span className="text-muted-foreground font-normal"> · {conv}%</span>}</span></div>
                        <div className="h-3 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-accent rounded-full" style={{ width: `${Math.round((f.count / maxFunnel) * 100)}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Cohort loop */}
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="cohort-loop">
                <div className="flex items-center gap-2 mb-4"><Repeat className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Cohort → Generator loop</h2></div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Lessons submitted</span><span className="font-bold">{ov.cohort_loop.submitted}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Approved</span><span className="font-bold">{ov.cohort_loop.approved}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tips feeding generator</span><span className="font-bold text-primary">{ov.cohort_loop.tips_feeding_generator}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Published to library</span><span className="font-bold">{ov.cohort_loop.published_to_library}</span></div>
                </div>
              </div>
            </div>

            {/* Engagement by subject */}
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="engagement-by-subject">
              <h2 className="font-display font-bold text-lg mb-4">Engagement by subject</h2>
              {ov.by_subject.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ov.by_subject.map((s) => (
                    <div key={s.subject} className="rounded-xl border border-border p-4">
                      <div className="font-semibold text-sm">{s.subject}</div>
                      <div className="text-xs text-muted-foreground mt-1">{s.lessons} lessons · {s.attempts} quizzes</div>
                      <div className="flex items-center gap-2 mt-2"><Progress value={s.avg_score} className="h-2" /><span className="text-xs font-bold w-9">{s.avg_score}%</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* LIBRARY — approved lessons only */}
          <TabsContent value="library" className="mt-6">
            {(() => {
              const subjects = Array.from(new Set(allLessons.map((l) => l.subject).filter(Boolean))).sort();
              const grades = Array.from(new Set(allLessons.map((l) => l.grade).filter(Boolean))).sort();
              const q = lessonSearch.trim().toLowerCase();
              const filtered = allLessons.filter((l) =>
                (lessonSubject === "all" || l.subject === lessonSubject) &&
                (lessonGrade === "all" || l.grade === lessonGrade) &&
                (!q || [l.title, l.subject, l.grade, l.state, l.topic, l.teacher_name].some((v) => (v || "").toLowerCase().includes(q)))
              );
              const statusMeta = (l) => {
                if (l.library_status === "approved") return { key: "approved", label: "In library", cls: "bg-emerald-500/15 text-emerald-700" };
                if (l.library_status === "submitted") return { key: "submitted", label: "Pending review", cls: "bg-amber-500/15 text-amber-700" };
                return { key: "draft", label: "Draft", cls: "bg-slate-500/15 text-slate-600" };
              };
              // build folders
              const folders = {};
              const order = libGroupBy === "status" ? ["approved", "submitted", "draft"] : [];
              filtered.forEach((l) => {
                const k = libGroupBy === "status" ? statusMeta(l).label : (l.teacher_name || "Unknown");
                (folders[k] = folders[k] || []).push(l);
              });
              const folderNames = libGroupBy === "status"
                ? ["In library", "Pending review", "Draft"].filter((n) => folders[n])
                : Object.keys(folders).sort();
              const card = (l) => {
                const sm = statusMeta(l);
                return (
                  <div key={l.id} data-testid={`all-lesson-${l.id}`} className="rounded-xl border border-border p-4 flex flex-col bg-background">
                    <div className="font-semibold text-sm">{l.title}</div>
                    <div className="text-xs text-muted-foreground">{l.subject} · {l.grade} · {l.state}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">by {l.teacher_name}</div>
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {l.style && <Badge variant="secondary" className="text-[10px]">{l.style}</Badge>}
                      <Badge className={`text-[10px] border-0 ${sm.cls}`}>{sm.label}</Badge>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                      <Button size="sm" variant="secondary" data-testid={`launch-all-lesson-${l.id}`} onClick={() => setLaunchLesson(l)} className="flex-1 h-8"><PlayCircle className="w-4 h-4 mr-1" /> Launch</Button>
                      <Button size="icon" variant="ghost" data-testid={`print-all-lesson-${l.id}`} onClick={() => printLesson(l)} className="h-8 w-8"><Printer className="w-4 h-4" /></Button>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`add-depth-${l.id}`} onClick={() => addDepth(l)} disabled={spatializing === l.id} className="w-full h-8 mt-2 rounded-full">
                      {spatializing === l.id ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Adding depth…</> : <><Layers className="w-3.5 h-3.5 mr-1" /> {l.vr_scene?.scenes?.[0]?.layers?.length ? "Re-add depth (beta)" : "Add depth (beta)"}</>}
                    </Button>
                  </div>
                );
              };
              return (
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="all-lessons">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2"><Library className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Lesson library <span className="text-sm font-normal text-muted-foreground">· {filtered.length} of {allLessons.length}</span></h2></div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={libGroupBy} onValueChange={setLibGroupBy}>
                      <SelectTrigger data-testid="lesson-groupby" className="h-9 w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="status">Group by status</SelectItem>
                        <SelectItem value="creator">Group by creator</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="lesson-search-input" value={lessonSearch} onChange={(e) => setLessonSearch(e.target.value)} placeholder="Search library…" className="h-9 w-48 pl-9" />
                    </div>
                    <Select value={lessonSubject} onValueChange={setLessonSubject}>
                      <SelectTrigger data-testid="lesson-subject-filter" className="h-9 w-32"><SelectValue placeholder="Subject" /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All subjects</SelectItem>{subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={lessonGrade} onValueChange={setLessonGrade}>
                      <SelectTrigger data-testid="lesson-grade-filter" className="h-9 w-28"><SelectValue placeholder="Grade" /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All grades</SelectItem>{grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center" data-testid="all-lessons-empty">{allLessons.length === 0 ? "No lessons have been built yet." : "No lessons match your filters."}</p>
                ) : (
                  <Accordion type="multiple" defaultValue={folderNames} className="space-y-3">
                    {folderNames.map((name) => (
                      <AccordionItem key={name} value={name} data-testid={`folder-${name.toLowerCase().replace(/\s+/g, '-')}`} className="border border-border rounded-xl px-4">
                        <AccordionTrigger className="hover:no-underline py-3">
                          <span className="flex items-center gap-2 text-sm font-semibold">
                            <Folder className="w-4 h-4 text-primary" /> {name}
                            <Badge variant="secondary" className="text-[10px] ml-1">{folders[name].length}</Badge>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {folders[name].map(card)}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
              );
            })()}
          </TabsContent>

          {/* MARKETING */}
          <TabsContent value="marketing" className="mt-6">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="company-campaigns">
              <div className="flex items-center gap-2 mb-4"><Megaphone className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Social ad campaigns</h2></div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="campaign-name-input" value={camp.name} onChange={(e) => setCamp({ ...camp, name: e.target.value })} placeholder="Campaign name" className="h-9" />
                    <Select value={camp.platform} onValueChange={(v) => setCamp({ ...camp, platform: v })}>
                      <SelectTrigger data-testid="campaign-platform-select" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={camp.objective} onChange={(e) => setCamp({ ...camp, objective: e.target.value })} placeholder="Objective" className="h-9" />
                    <Input type="number" value={camp.budget} onChange={(e) => setCamp({ ...camp, budget: e.target.value })} placeholder="Budget $" className="h-9" />
                  </div>
                  <Input value={camp.audience} onChange={(e) => setCamp({ ...camp, audience: e.target.value })} placeholder="Target audience" className="h-9" />
                  {camp.name && <div className="text-[11px] font-mono bg-secondary rounded-lg px-3 py-2 flex items-center gap-2 break-all"><Link2 className="w-3.5 h-3.5 shrink-0" /> {utmFor(camp.name, camp.platform)}</div>}
                  <div className="flex gap-2">
                    <Button data-testid="campaign-gencopy-btn" onClick={generateCopy} disabled={genLoading} variant="secondary" className="flex-1 rounded-full">
                      {genLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> 3 AI variants + image</>}
                    </Button>
                    <Button data-testid="campaign-save-btn" onClick={saveCampaign} className="flex-1 rounded-full">Save</Button>
                  </div>
                  {image && <img src={imgUrl(image)} alt="creative" className="w-full h-36 object-cover rounded-lg" />}
                  {variants && (
                    <div className="space-y-2">
                      {variants.map((v, i) => (
                        <button key={i} data-testid={`ad-variant-${i}`} onClick={() => setChosen(v)} className={`w-full text-left rounded-xl border-2 p-3 transition-colors ${chosen === v ? "border-primary bg-primary/5" : "border-border"}`}>
                          <div className="font-display font-bold text-sm">{v.headline}</div>
                          <p className="text-xs text-muted-foreground mt-0.5">{v.body}</p>
                          <div className="text-[11px] text-primary font-semibold mt-1">{v.cta} · {v.hashtags}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2 max-h-[560px] overflow-auto">
                  {campaigns.length === 0 && <p className="text-sm text-muted-foreground">No campaigns yet — draft one on the left.</p>}
                  {campaigns.map((c) => {
                    const ctr = c.impressions ? ((c.clicks / c.impressions) * 100).toFixed(1) : "0.0";
                    const cps = c.clicks ? (c.spend / c.clicks).toFixed(2) : "—";
                    return (
                      <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-xl border border-border p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-muted-foreground">{c.platform}{c.budget ? ` · $${c.budget}` : ""}</div></div>
                          <Badge className={`${c.status === "launched" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"} border-0 text-[10px]`}>{c.status}</Badge>
                        </div>
                        {c.headline && <div className="text-sm mt-2 font-medium">{c.headline}</div>}
                        {c.utm && <button onClick={() => copyText(c.utm)} className="text-[10px] font-mono text-muted-foreground hover:text-primary mt-1 flex items-center gap-1"><Copy className="w-3 h-3" /> copy tracked link</button>}
                        <div className="grid grid-cols-3 gap-1.5 mt-3">
                          <Input type="number" defaultValue={c.impressions || 0} placeholder="Impr." className="h-8 text-xs" id={`imp-${c.id}`} />
                          <Input type="number" defaultValue={c.clicks || 0} placeholder="Clicks" className="h-8 text-xs" id={`clk-${c.id}`} />
                          <Input type="number" defaultValue={c.spend || 0} placeholder="Spend" className="h-8 text-xs" id={`spd-${c.id}`} />
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-1.5"><span>CTR {ctr}%</span><span>Cost/click ${cps}</span></div>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" data-testid={`campaign-metrics-${c.id}`} onClick={() => saveMetrics(c, { impressions: +document.getElementById(`imp-${c.id}`).value, clicks: +document.getElementById(`clk-${c.id}`).value, spend: +document.getElementById(`spd-${c.id}`).value })} className="flex-1 h-8 rounded-full text-xs">Save metrics</Button>
                          {c.status !== "launched" && <Button size="sm" data-testid={`campaign-launch-${c.id}`} onClick={() => launch(c.id)} className="flex-1 h-8 rounded-full text-xs"><Rocket className="w-3.5 h-3.5 mr-1" /> Launch</Button>}
                          <Button size="icon" variant="ghost" onClick={() => delCampaign(c.id)} className="h-8 w-8 text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* COHORT */}
          <TabsContent value="cohort" className="mt-6 space-y-6">
            {/* Loop stats + Skool link */}
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6" data-testid="cohort-loop-stats">
                <div className="flex items-center gap-2 mb-4"><Repeat className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Cohort → Generator loop</h2></div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Submitted", val: ov.cohort_loop.submitted, icon: MessageSquare, c: "text-primary bg-primary/10" },
                    { label: "Approved", val: ov.cohort_loop.approved, icon: Check, c: "text-emerald-600 bg-emerald-500/10" },
                    { label: "Tips feeding generator", val: ov.cohort_loop.tips_feeding_generator, icon: Lightbulb, c: "text-amber-600 bg-amber-500/10" },
                    { label: "Published to library", val: ov.cohort_loop.published_to_library, icon: BookOpen, c: "text-cyan-600 bg-cyan-500/10" },
                  ].map((k) => (
                    <div key={k.label} className="rounded-xl border border-border p-4" data-testid={`cohort-stat-${slug(k.label)}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.c}`}><k.icon className="w-4 h-4" /></div>
                      <div className="text-2xl font-display font-extrabold mt-2">{k.val}</div>
                      <div className="text-xs text-muted-foreground leading-tight">{k.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6" data-testid="skool-community">
                <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Skool community</h2></div>
                <p className="text-sm text-muted-foreground mb-3">Link your cohort's Skool community blog for quick access.</p>
                <Label className="text-xs">Community URL</Label>
                <Input data-testid="skool-url-input" value={settings.skool_url} onChange={(e) => setSettings({ ...settings, skool_url: e.target.value })} placeholder="https://www.skool.com/your-community" className="h-9 mt-1" />
                <div className="flex gap-2 mt-3">
                  <Button data-testid="skool-save-btn" variant="outline" onClick={saveSkool} className="flex-1 rounded-full h-9">Save</Button>
                  <Button data-testid="skool-open-btn" onClick={openSkool} className="flex-1 rounded-full h-9"><ExternalLink className="w-4 h-4 mr-1" /> Open</Button>
                </div>
              </div>
            </div>

            {/* Lessons pending review */}
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="cohort-pending-lessons">
              <div className="flex items-center gap-2 mb-1"><Layers className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Lessons pending review</h2></div>
              <p className="text-sm text-muted-foreground mb-4">Lessons submitted by cohorts &amp; teachers awaiting your review. Select one to launch it, review the suggested edits, regenerate, or approve it to the shareable library.</p>
              {(() => {
                const pending = allLessons.filter((l) => l.library_status === "submitted");
                if (pending.length === 0) return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="cohort-pending-empty">No lessons are waiting for review right now.</p>;
                return (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pending.map((l) => (
                      <button key={l.id} data-testid={`pending-lesson-${l.id}`} onClick={() => openReview(l)} className="text-left rounded-xl border border-border p-4 hover:border-primary hover:shadow-md transition-all">
                        <div className="font-semibold text-sm">{l.title}</div>
                        <div className="text-xs text-muted-foreground">{l.subject} · {l.grade} · {l.state}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">by {l.teacher_name}</div>
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {l.style && <Badge variant="secondary" className="text-[10px]">{l.style}</Badge>}
                          {l.library_status === "submitted" && <Badge className="text-[10px] bg-amber-500/15 text-amber-700 border-0">Submitted</Badge>}
                          {l.regen_status === "running" && <Badge className="text-[10px] bg-blue-500/15 text-blue-700 border-0">Regenerating…</Badge>}
                        </div>
                        <div className="text-xs text-primary font-medium mt-3 flex items-center gap-1">Review <ArrowRight className="w-3 h-3" /></div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Approval queue */}
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="cohort-queue">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Lesson submissions</h2></div>
                <Select value={contribFilter} onValueChange={changeContribFilter}>
                  <SelectTrigger data-testid="contrib-filter-select" className="h-9 w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_review">Pending review</SelectItem>
                    <SelectItem value="needs_manual_review">Needs manual review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {contribs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="cohort-empty">No submissions in this view.</p>
              ) : (
                <div className="space-y-3">
                  {contribs.map((c) => {
                    const pending = c.status === "pending_review" || c.status === "needs_manual_review";
                    return (
                      <div key={c.id} data-testid={`contrib-${c.id}`} className="rounded-xl border border-border p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-sm">{c.title || "Untitled lesson"}</div>
                            <div className="text-xs text-muted-foreground">{c.subject || "—"} · Grade {c.grade || "—"} · by {c.author || "Unknown"}</div>
                          </div>
                          <Badge className={`border-0 text-[10px] shrink-0 ${
                            c.status === "approved" ? "bg-emerald-500/15 text-emerald-700"
                            : c.status === "rejected" ? "bg-red-500/15 text-red-700"
                            : "bg-amber-500/15 text-amber-700"}`}>{(c.status || "").replace(/_/g, " ")}</Badge>
                        </div>
                        {c.feedback && <p className="text-xs text-muted-foreground mt-2 italic">Teacher note: {c.feedback}</p>}
                        {c.suggestions?.length > 0 && (
                          <div className="mt-3 rounded-lg bg-secondary/60 p-3">
                            <div className="text-xs font-semibold flex items-center gap-1 mb-1"><Lightbulb className="w-3.5 h-3.5 text-amber-600" /> AI improvement tips</div>
                            <ul className="list-disc pl-5 space-y-0.5">
                              {c.suggestions.map((t, i) => <li key={i} className="text-xs text-muted-foreground">{t}</li>)}
                            </ul>
                          </div>
                        )}
                        {pending && (
                          <div className="flex gap-2 mt-3">
                            <Button size="sm" data-testid={`contrib-approve-${c.id}`} onClick={() => approveContrib(c.id)} className="rounded-full h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                            <Button size="sm" variant="outline" data-testid={`contrib-reject-${c.id}`} onClick={() => rejectContrib(c.id)} className="rounded-full h-8 text-xs text-destructive"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ACCOUNTS */}
          <TabsContent value="accounts" className="mt-6 space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="accounts-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <h2 className="font-display font-bold text-lg">Accounts <span className="text-sm font-normal text-muted-foreground">· {users.length} total</span></h2>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="accounts-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search name or email" className="pl-8 h-9 w-56" />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="h-9 w-36" data-testid="accounts-role-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All roles</SelectItem>
                      <SelectItem value="teacher">Teachers</SelectItem>
                      <SelectItem value="cohort">Cohort</SelectItem>
                      <SelectItem value="student">Students</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 max-h-[560px] overflow-auto">
                {users
                  .filter((u) => roleFilter === "all" || u.role === roleFilter)
                  .filter((u) => {
                    const q = userSearch.trim().toLowerCase();
                    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                  })
                  .map((u) => (
                    <div key={u.id} data-testid={`account-row-${u.id}`} className={`rounded-xl border p-3 ${u.status === "frozen" ? "border-amber-400/60 bg-amber-500/5" : "border-border"}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm truncate">{u.name || "—"}</span>
                            {u.status === "frozen" && <Badge className="text-[9px] bg-amber-500/20 text-amber-700 border-0">Frozen</Badge>}
                            {u.role === "admin" && <Badge className="text-[9px] bg-primary/15 text-primary border-0">Admin</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{u.email}{u.school ? ` · ${u.school}` : ""}</div>
                          <div className="text-[11px] text-muted-foreground mt-1">
                            {u.lessons_built} lessons · {u.students_count} students · {u.quiz_attempts} quizzes · joined {(u.created_at || "").slice(0, 10)}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Select value={u.role} onValueChange={(v) => doChangeRole(u, v)}>
                            <SelectTrigger className="h-8 w-28 text-xs" data-testid={`account-role-${u.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="teacher">Teacher</SelectItem>
                              <SelectItem value="cohort">Cohort</SelectItem>
                              <SelectItem value="student">Student</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={u.plan || "free"} onValueChange={(v) => doChangePlan(u, v)}>
                            <SelectTrigger className="h-8 w-28 text-xs" data-testid={`account-plan-${u.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="family">Family</SelectItem>
                              <SelectItem value="classroom">Classroom</SelectItem>
                              <SelectItem value="founding">Founding</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        <Button size="sm" variant="outline" data-testid={`account-resetpw-${u.id}`} onClick={() => doResetPw(u, "temp")} className="h-8 rounded-full text-xs">
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Temp password
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`account-resetlink-${u.id}`} onClick={() => doResetPw(u, "email")} className="h-8 rounded-full text-xs">
                          <Send className="w-3.5 h-3.5 mr-1" /> Email reset link
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`account-freeze-${u.id}`} onClick={() => doFreeze(u, u.status !== "frozen")} className="h-8 rounded-full text-xs">
                          {u.status === "frozen" ? <><Check className="w-3.5 h-3.5 mr-1" /> Unfreeze</> : <><Snowflake className="w-3.5 h-3.5 mr-1" /> Freeze</>}
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`account-delete-${u.id}`} onClick={() => { setDeleteUser(u); setEraseData(false); }} className="h-8 rounded-full text-xs text-destructive hover:text-destructive">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Lesson Trash */}
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="trash-panel">
              <div className="flex items-center gap-2 mb-1">
                <Trash2 className="w-5 h-5 text-amber-600" />
                <h2 className="font-display font-bold text-lg">Deleted lessons (Trash) <span className="text-sm font-normal text-muted-foreground">· {trash.length}</span></h2>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Deleted lessons land here so nothing is ever permanently lost by accident. Restore any lesson, or delete it forever.</p>
              {trash.length === 0 ? (
                <p className="text-sm text-muted-foreground">Trash is empty. Deleted lessons will appear here.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-auto">
                  {trash.map((l) => (
                    <div key={l.id} data-testid={`trash-row-${l.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{l.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{l.subject}{l.grade ? ` · ${l.grade}` : ""} · by {l.teacher_name} · deleted {(l.deleted_at || "").slice(0, 10)}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" data-testid={`trash-restore-${l.id}`} onClick={() => restoreLesson(l.id)} className="h-8 rounded-full text-xs">
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Restore
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`trash-purge-${l.id}`} onClick={() => purgeLesson(l.id)} className="h-8 w-8 p-0 text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* OPS */}
          <TabsContent value="ops" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="company-tasks">
                <div className="flex items-center gap-2 mb-4"><CheckSquare className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Tasks</h2></div>
                <div className="flex gap-2 mb-4">
                  <Input data-testid="task-title-input" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} placeholder="New task…" className="h-9" onKeyDown={(e) => e.key === "Enter" && addTask()} />
                  <Input type="date" value={newTask.due} onChange={(e) => setNewTask({ ...newTask, due: e.target.value })} className="h-9 w-36" />
                  <Button size="sm" data-testid="task-add-btn" onClick={addTask}><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-2 max-h-80 overflow-auto">
                  {tasks.length === 0 && <p className="text-sm text-muted-foreground">No tasks yet.</p>}
                  {tasks.map((t) => {
                    const overdue = t.due && !t.done && t.due < new Date().toISOString().slice(0, 10);
                    return (
                      <div key={t.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                        <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`}>{t.done ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4 text-muted-foreground" />}</button>
                        <span className={`flex-1 text-sm ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                        {t.due && <span className={`text-xs ${overdue ? "text-red-600 font-bold" : "text-muted-foreground"}`}>{overdue ? "overdue" : t.due}</span>}
                        <button onClick={() => delTask(t.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6" data-testid="company-meetings">
                <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Meetings</h2></div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input data-testid="meeting-title-input" value={newMeeting.title} onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })} placeholder="Meeting title" className="h-9" />
                  <Input data-testid="meeting-when-input" type="datetime-local" value={newMeeting.when} onChange={(e) => setNewMeeting({ ...newMeeting, when: e.target.value })} className="h-9" />
                </div>
                <div className="flex gap-2 mb-4">
                  <Input value={newMeeting.attendees} onChange={(e) => setNewMeeting({ ...newMeeting, attendees: e.target.value })} placeholder="Attendees (optional)" className="h-9" />
                  <Button size="sm" data-testid="meeting-add-btn" onClick={addMeeting}><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-2 max-h-72 overflow-auto">
                  {meetings.length === 0 && <p className="text-sm text-muted-foreground">No meetings scheduled.</p>}
                  {meetings.map((m) => {
                    const soon = new Date(m.when) - new Date() < 86400000 && new Date(m.when) > new Date();
                    return (
                      <div key={m.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                        <div><div className="font-semibold text-sm flex items-center gap-2">{m.title}{soon && <Badge className="bg-primary/15 text-primary border-0 text-[9px]">soon</Badge>}</div><div className="text-xs text-muted-foreground">{new Date(m.when).toLocaleString()}{m.attendees && ` · ${m.attendees}`}</div></div>
                        <button onClick={() => delMeeting(m.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Review dialog */}
      <Dialog open={!!reviewLesson} onOpenChange={(o) => !o && !regenRunning && setReviewLesson(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-primary" /> {reviewLesson?.title}</DialogTitle>
            <DialogDescription>{reviewLesson?.subject} · {reviewLesson?.grade} · {reviewLesson?.state} · by {reviewLesson?.teacher_name}</DialogDescription>
          </DialogHeader>
          {reviewLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading review…</div>
          ) : (
            <div className="space-y-5">
              {/* Storyboard preview — what the cohort is remarking on */}
              <div>
                <div className="text-sm font-semibold flex items-center gap-1 mb-2"><Film className="w-4 h-4 text-primary" /> Storyboard {reviewData?.lesson?.regen_status === "done" && <Badge className="text-[9px] bg-blue-500/15 text-blue-700 border-0 ml-1">Latest regeneration</Badge>}</div>
                {(reviewData?.lesson?.documents?.storyboard || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No storyboard scenes on this lesson yet.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="review-storyboard">
                    {reviewData.lesson.documents.storyboard.map((sc, i) => (
                      <div key={i} className="rounded-lg border border-border overflow-hidden bg-secondary/40">
                        <div className="aspect-video relative overflow-hidden bg-secondary">
                          {sc.image_url ? (
                            <img src={imgUrl(sc.image_url)} alt={sc.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Film className="w-5 h-5" /></div>
                          )}
                          <Badge className="absolute top-1.5 left-1.5 bg-black/70 text-white border-0 text-[9px]">Scene {sc.scene || i + 1}</Badge>
                        </div>
                        <div className="p-2">
                          <div className="text-xs font-semibold leading-tight line-clamp-1">{sc.title}</div>
                          <div className="text-[11px] text-muted-foreground leading-snug line-clamp-2 mt-0.5">{sc.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button size="sm" variant="secondary" data-testid="review-launch-inline" onClick={() => reviewData?.lesson && setLaunchLesson(reviewData.lesson)} disabled={!reviewData?.lesson?.vr_scene || regenRunning} className="w-full h-9 mt-3 rounded-full">
                  <PlayCircle className="w-4 h-4 mr-1" /> {reviewData?.lesson?.vr_scene ? "Launch full VR lesson" : "No VR scene to launch yet"}
                </Button>
              </div>

              {/* Cohort suggestions */}
              <div>
                <div className="text-sm font-semibold flex items-center gap-1 mb-2"><Lightbulb className="w-4 h-4 text-amber-600" /> Cohort's suggested edits</div>
                {(reviewData?.cohort_suggestions || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No cohort suggestions on record for this lesson.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="review-cohort-suggestions">
                    {reviewData.cohort_suggestions.map((s, i) => (
                      <li key={i} className="text-sm rounded-lg bg-secondary/60 p-2.5 flex items-start gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />{s}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Add own suggestions */}
              <div>
                <div className="text-sm font-semibold mb-1">Add your own suggestions <span className="text-muted-foreground font-normal">(one per line, optional)</span></div>
                <Textarea data-testid="review-admin-suggestions" value={adminSug} onChange={(e) => setAdminSug(e.target.value)} placeholder="e.g. Add a checkpoint quiz after scene 2" className="min-h-[70px]" />
              </div>

              {/* History */}
              <div>
                <div className="text-sm font-semibold flex items-center gap-1 mb-2"><History className="w-4 h-4 text-primary" /> Approval & generator history</div>
                <div className="space-y-1.5 text-xs" data-testid="review-history">
                  {(reviewData?.contributions || []).map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-1.5">
                      <span>Submitted by {c.author || "Unknown"}{c.feedback ? ` — "${c.feedback}"` : ""}</span>
                      <Badge className={`border-0 text-[9px] ${c.status === "approved" ? "bg-emerald-500/15 text-emerald-700" : c.status === "rejected" ? "bg-red-500/15 text-red-700" : "bg-amber-500/15 text-amber-700"}`}>{(c.status || "").replace(/_/g, " ")}</Badge>
                    </div>
                  ))}
                  {(reviewData?.regen_history || []).map((h, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-blue-500/20 bg-blue-500/5 px-3 py-1.5">
                      <span>Regenerated with {h.suggestions?.length || 0} edit(s)</span>
                      <span className="text-muted-foreground">{new Date(h.at).toLocaleDateString()}</span>
                    </div>
                  ))}
                  {(reviewData?.contributions || []).length === 0 && (reviewData?.regen_history || []).length === 0 && (
                    <p className="text-muted-foreground">No history yet.</p>
                  )}
                </div>
              </div>

              {/* Check-in insights */}
              {checkinInsights.length > 0 && (
                <div data-testid="review-checkin-insights">
                  <div className="text-sm font-semibold flex items-center gap-1 mb-2"><HelpCircle className="w-4 h-4 text-amber-600" /> Pause &amp; ask insights <span className="text-muted-foreground font-normal">· student responses</span></div>
                  <div className="space-y-1.5 text-xs">
                    {checkinInsights.map((c, i) => (
                      <div key={i} className="rounded-lg border border-border px-3 py-2">
                        <div className="font-medium line-clamp-1">S{c.scene_index + 1} · Step {c.beat_index + 1}: {c.prompt || (c.kind === "mcq" ? "Comprehension check" : "Check-in")}</div>
                        <div className="flex items-center gap-3 mt-1 text-[11px]">
                          {c.kind === "mcq" ? (
                            <>
                              <span className="text-emerald-600 font-semibold">✓ {c.mcq_correct} correct</span>
                              <span className="text-red-600 font-semibold">✗ {c.mcq_incorrect} incorrect</span>
                            </>
                          ) : (
                            <>
                              <span className="text-emerald-600 font-semibold flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {c.got_it} got it</span>
                              <span className="text-amber-600 font-semibold">🤔 {c.confused} confused</span>
                            </>
                          )}
                          <span className="text-muted-foreground ml-auto">{c.total} response{c.total === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            <Button variant="secondary" data-testid="review-launch-btn" onClick={() => reviewData?.lesson && setLaunchLesson(reviewData.lesson)} disabled={reviewLoading || regenRunning || !reviewData?.lesson?.vr_scene} className="rounded-full mr-auto">
              <PlayCircle className="w-4 h-4 mr-1" /> Launch to review
            </Button>
            <Button variant="outline" data-testid="review-editbeats-btn" onClick={openBeatEditor} disabled={reviewLoading || regenRunning || !reviewData?.lesson?.vr_scene} className="rounded-full">
              <ListOrdered className="w-4 h-4 mr-1" /> Edit timeline
            </Button>
            <Button variant="outline" data-testid="review-adddepth-btn" onClick={() => reviewLesson && addDepth(reviewLesson)} disabled={spatializing === reviewLesson?.id || regenRunning || approving || reviewLoading} className="rounded-full">
              {spatializing === reviewLesson?.id ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Adding depth…</> : <><Layers className="w-4 h-4 mr-1" /> Add depth</>}
            </Button>
            <Button variant="outline" data-testid="review-guide-btn" onClick={() => reviewLesson && addGuide(reviewLesson)} disabled={guiding === reviewLesson?.id || regenRunning || approving || reviewLoading} className="rounded-full">
              {guiding === reviewLesson?.id ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating guide…</> : <><Sparkles className="w-4 h-4 mr-1" /> Add talking guide (Founding)</>}
            </Button>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" data-testid="review-checkins-toggle" checked={includeCheckins} onChange={(e) => setIncludeCheckins(e.target.checked)} className="accent-primary w-3.5 h-3.5" />
                Add pause &amp; ask check-ins
              </label>
              <Button variant="outline" data-testid="review-sequence-btn" onClick={() => reviewLesson && sequenceBeats(reviewLesson, includeCheckins)} disabled={sequencing === reviewLesson?.id || regenRunning || approving || reviewLoading} className="rounded-full">
                {sequencing === reviewLesson?.id ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sequencing…</> : <><Route className="w-4 h-4 mr-1" /> Smart sequence (AI)</>}
              </Button>
            </div>
            <Button variant="outline" data-testid="review-regenerate-btn" onClick={doRegenerate} disabled={regenRunning || approving || reviewLoading} className="rounded-full">
              {regenRunning ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Regenerating…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Regenerate with edits</>}
            </Button>
            <Button data-testid="review-approve-btn" onClick={doApprove} disabled={regenRunning || approving || reviewLoading} className="rounded-full">
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve & post to library</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Beat editor dialog */}
      <Dialog open={beatEditorOpen} onOpenChange={(o) => !o && setBeatEditorOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="beat-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListOrdered className="w-5 h-5 text-primary" /> Edit lesson timeline</DialogTitle>
            <DialogDescription>Drag the handle to reorder how each scene reveals to students. Edit the spoken text, or remove a beat.</DialogDescription>
          </DialogHeader>
          {editBeats.length > 1 && (
            <div className="flex gap-1.5 flex-wrap" data-testid="beat-editor-scene-tabs">
              {editBeats.map((_, i) => (
                <button
                  key={i}
                  data-testid={`beat-editor-scene-${i}`}
                  onClick={() => setEditSceneIdx(i)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${editSceneIdx === i ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
                >
                  Scene {i + 1}
                </button>
              ))}
            </div>
          )}
          {(editBeats[editSceneIdx] || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No beats on this scene.</p>
          ) : (
            <Reorder.Group
              axis="y"
              values={editBeats[editSceneIdx] || []}
              onReorder={reorderScene}
              className="space-y-2"
              data-testid="beat-editor-list"
            >
              {(editBeats[editSceneIdx] || []).map((beat, bi) => (
                <BeatRow
                  key={beat._id}
                  beat={beat}
                  onChange={(v) => updateBeat(bi, v)}
                  onRemove={() => removeBeat(bi)}
                />
              ))}
            </Reorder.Group>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBeatEditorOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="beat-editor-save-btn" onClick={saveBeats} disabled={savingBeats} className="rounded-full">
              {savingBeats ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Check className="w-4 h-4 mr-1" /> Save timeline</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete account confirm dialog */}
      <Dialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 className="w-5 h-5" /> Delete account</DialogTitle>
            <DialogDescription>
              Permanently remove <strong>{deleteUser?.name}</strong> ({deleteUser?.email})? They will no longer be able to log in.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer select-none rounded-lg border border-border p-3">
            <input type="checkbox" data-testid="delete-erase-data" checked={eraseData} onChange={(e) => setEraseData(e.target.checked)} className="accent-destructive w-4 h-4 mt-0.5" />
            <span>
              <span className="font-semibold">Also erase all their data</span>
              <span className="block text-xs text-muted-foreground">Deletes their lessons, students, quiz scores and check-ins. Leave unchecked to keep their lessons (they'll be marked orphaned and recoverable from Trash).</span>
            </span>
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteUser(null)} className="rounded-full">Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-user-btn" onClick={confirmDeleteUser} className="rounded-full">
              <Trash2 className="w-4 h-4 mr-1" /> {eraseData ? "Delete + erase data" : "Delete account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password result dialog */}
      <Dialog open={!!tempPwResult} onOpenChange={(o) => !o && setTempPwResult(null)}>
        <DialogContent className="max-w-md" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary" /> Temporary password</DialogTitle>
            <DialogDescription>Share this with {tempPwResult?.email}. They should change it after signing in.{tempPwResult?.emailed ? " (Also emailed to them.)" : ""}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code data-testid="temp-password-value" className="flex-1 text-lg font-bold tracking-wide bg-secondary rounded-lg px-4 py-3 text-center">{tempPwResult?.password}</code>
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(tempPwResult?.password || ""); toast.success("Copied"); }} className="rounded-full"><Copy className="w-4 h-4" /></Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPwResult(null)} className="rounded-full">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Launch dialog */}
      <Dialog open={!!launchLesson} onOpenChange={(o) => !o && setLaunchLesson(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{launchLesson?.title}</DialogTitle>
            <DialogDescription>{launchLesson?.state} · {launchLesson?.grade} · {launchLesson?.subject} · {launchLesson?.topic} · by {launchLesson?.teacher_name}</DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl overflow-hidden border border-slate-800 bg-[#0B0F19] h-[440px]" data-testid="company-launch-vr-container">
            {launchLesson && <VRViewer scene={launchLesson.vr_scene} quiz={launchLesson.documents?.assessment?.questions || []} lessonId={launchLesson.id} students={[]} onQuizSubmitted={() => {}} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
